import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

import { LanguageService } from '../../services/language.service';
import { BannerService, BANNER_SLOTS } from '../../services/banner.service';
import {
  HomepageLayoutService,
  HomeSection,
  SectionMeta,
  SECTION_META,
  defaultSections
} from '../../services/homepage-layout.service';

import { AdminPopularBrands } from '../admin-popular-brands/admin-popular-brands';
import { AdminFeatures } from '../admin-features/admin-features';
import { AdminShopMore } from '../admin-shop-more/admin-shop-more';

type Tab = 'layout' | 'categories' | 'brands' | 'features' | 'shopmore';

interface UploadState {
  file: File | null;
  linkUrl: string;
  busy: boolean;
}

// অ্যাডমিন → হোমপেজ ডিজাইন
// এক জায়গা থেকে হোমপেজের সব কিছু: সেকশনের ক্রম / দেখানো-লুকানো / টাইটেল / আইটেম সংখ্যা,
// প্রতিটা সেকশনের নিচের ব্যানার, পপুলার ক্যাটাগরি, পপুলার ব্র্যান্ড, ফিচার বার, শপ মোর।
@Component({
  selector: 'app-admin-homepage-design',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AdminPopularBrands,
    AdminFeatures,
    AdminShopMore
  ],
  templateUrl: './admin-homepage-design.html',
  styleUrls: ['./admin-homepage-design.css']
})
export class AdminHomepageDesign implements OnInit, OnDestroy {

  readonly host = 'https://superbangladesh-api-1.onrender.com';

  tab: Tab = 'layout';

  // -------- সেকশন লেআউট --------
  metaByKey = new Map<string, SectionMeta>(SECTION_META.map(m => [m.key, m]));

  // শুরুতেই ডিফল্ট সেকশন দিয়ে ভরা — সার্ভার দেরি করলেও পেজ ফাঁকা থাকে না
  sections: HomeSection[] = defaultSections();
  loadingSections = true;
  serverOffline = false;
  saving = false;
  dirty = false;
  notice = '';
  noticeError = false;
  private noticeTimer: any;

  // -------- ব্যানার --------
  banners: any[] = [];
  uploads: Record<string, UploadState> = {};

  // -------- পপুলার ক্যাটাগরি --------
  popular: any[] = [];
  loadingPopular = false;
  popularBusyId: number | null = null;

  search = '';
  results: any[] = [];
  searching = false;
  searched = false;
  private searchTimer: any;

  constructor(
    private http: HttpClient,
    private layout: HomepageLayoutService,
    private bannerService: BannerService,
    public languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  get bn(): boolean {
    return this.languageService.language() === 'bn';
  }

  tr(bn: string, en: string): string {
    return this.bn ? bn : en;
  }

  ngOnInit(): void {
    this.loadSections();
    this.loadBanners();
  }

  ngOnDestroy(): void {
    clearTimeout(this.searchTimer);
    clearTimeout(this.noticeTimer);
  }

  setTab(t: Tab): void {

    this.tab = t;

    if (t === 'categories' && !this.popular.length && !this.loadingPopular) {
      this.loadPopular();
    }
  }

  private toast(msg: string, error = false): void {

    this.notice = msg;
    this.noticeError = error;

    clearTimeout(this.noticeTimer);

    this.noticeTimer = setTimeout(() => {
      this.notice = '';
      this.cdr.detectChanges();
    }, 3500);

    this.cdr.detectChanges();
  }

  /* =========================================================
     সেকশন লেআউট
  ========================================================= */

  metaOf(key: string): SectionMeta {

    return this.metaByKey.get(key) || {
      key,
      icon: '📦',
      nameBn: key,
      nameEn: key,
      limit: 'none',
      titleBn: key,
      titleEn: key,
      defaultLimit: null,
      hasTitle: true
    };
  }

  nameOf(s: HomeSection): string {

    const m = this.metaOf(s.sectionKey);

    return this.bn ? m.nameBn : m.nameEn;
  }

  loadSections(): void {

    this.loadingSections = true;

    this.layout.getSections(true).subscribe({

      next: list => {

        this.sections = list.map(s => ({ ...s }));

        this.serverOffline = this.layout.lastLoadFailed;
        this.dirty = false;
        this.loadingSections = false;

        this.cdr.detectChanges();
      },

      error: () => {

        this.serverOffline = true;
        this.loadingSections = false;

        this.cdr.detectChanges();
      }
    });
  }

  markDirty(): void {
    this.dirty = true;
  }

  canMoveUp(i: number): boolean {

    const s = this.sections[i];

    if (!s || this.metaOf(s.sectionKey).pinnedTop) {
      return false;
    }

    // মূল ব্যানার সবসময় সবার উপরে — তার উপরে কিছু উঠবে না
    const above = this.sections[i - 1];

    return !!above && !this.metaOf(above.sectionKey).pinnedTop;
  }

  canMoveDown(i: number): boolean {

    const s = this.sections[i];

    return !!s
      && !this.metaOf(s.sectionKey).pinnedTop
      && i < this.sections.length - 1;
  }

  move(i: number, dir: -1 | 1): void {

    const j = i + dir;

    if (dir === -1 ? !this.canMoveUp(i) : !this.canMoveDown(i)) {
      return;
    }

    const copy = [...this.sections];

    [copy[i], copy[j]] = [copy[j], copy[i]];

    this.sections = copy;

    this.markDirty();
  }

  limitLabel(s: HomeSection): string {

    const kind = this.metaOf(s.sectionKey).limit;

    if (kind === 'rows') {
      return this.tr('প্রথমে কয় সারি দেখাবে', 'Rows shown first');
    }

    return this.tr('সর্বোচ্চ কয়টা দেখাবে (ফাঁকা = সব)', 'Max items (empty = all)');
  }

  saveLayout(): void {

    if (this.saving) {
      return;
    }

    this.saving = true;

    this.layout.save(this.sections).subscribe({

      next: list => {

        this.sections = list.map(s => ({ ...s }));

        this.saving = false;
        this.dirty = false;

        this.toast(this.tr('✅ হোমপেজ ডিজাইন সেভ হয়েছে', '✅ Homepage design saved'));
      },

      error: () => {

        this.saving = false;

        this.toast(
          this.tr(
            '❌ সেভ হয়নি। ব্যাকএন্ড আপডেট হয়েছে কি না এবং অ্যাডমিন হিসেবে লগইন আছে কি না দেখুন।',
            '❌ Could not save. Check the backend is updated and you are logged in as admin.'
          ),
          true
        );
      }
    });
  }

  resetLayout(): void {

    const ok = confirm(
      this.tr(
        'সব সেকশনের ক্রম, টাইটেল ও সেটিং ডিফল্টে ফিরবে। (ব্যানার মুছবে না) — নিশ্চিত?',
        'Reset order, titles and settings to default? (Banners are kept)'
      )
    );

    if (!ok) {
      return;
    }

    this.layout.reset().subscribe({

      next: list => {

        this.sections = list.map(s => ({ ...s }));

        this.dirty = false;

        this.toast(this.tr('✅ ডিফল্টে ফেরানো হয়েছে', '✅ Reset to default'));
      },

      error: () =>
        this.toast(this.tr('❌ রিসেট হয়নি', '❌ Reset failed'), true)
    });
  }

  /* =========================================================
     ব্যানার (প্রতিটা সেকশনের নিচে)
  ========================================================= */

  // একটা সেকশনের কোন কোন ব্যানার স্লট আছে
  // ⚠️ HANG FIX: আগে প্রতিবার নতুন অবজেক্ট বানিয়ে ফেরত দিত। *ngFor প্রতিবার সব স্লট
  // ভেঙে নতুন করে বানাত → ভেতরের ngModel আবার change detection চালু করত → অনন্ত লুপ → পেজ হ্যাং।
  // এখন একই অবজেক্ট ক্যাশে রাখা হয় (ভাষা বদলালে আলাদা ক্যাশ) + ngFor-এ trackBy আছে।
  private slotCache = new Map<string, { key: string; label: string }[]>();

  slotsOf(s: HomeSection): { key: string; label: string }[] {

    const cacheKey = s.sectionKey + '|' + (this.bn ? 'bn' : 'en');

    const cached = this.slotCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    const keys = s.sectionKey === 'HERO'
      ? ['HERO', 'AFTER_HERO']
      : ['AFTER_' + s.sectionKey];

    const slots = keys.map(k => {

      const def = BANNER_SLOTS.find(b => b.key === k);

      return {
        key: k,
        label: def
          ? (this.bn ? def.labelBn : def.labelEn)
          : k
      };
    });

    this.slotCache.set(cacheKey, slots);

    return slots;
  }

  trackSlot(index: number, slot: { key: string }): string {
    return slot.key;
  }

  trackBanner(index: number, b: any): any {
    return b?.id ?? index;
  }

  upload(slot: string): UploadState {

    if (!this.uploads[slot]) {
      this.uploads[slot] = { file: null, linkUrl: '', busy: false };
    }

    return this.uploads[slot];
  }

  bannersIn(slot: string): any[] {
    return this.banners.filter(b => (b.slot || 'HERO') === slot);
  }

  loadBanners(): void {

    this.http.get<any[]>(`${this.host}/api/banners`).subscribe({

      next: res => {
        this.banners = Array.isArray(res) ? res : [];
        this.cdr.detectChanges();
      },

      error: () => {
        this.banners = [];
        this.cdr.detectChanges();
      }
    });
  }

  pickFile(slot: string, event: Event): void {

    const input = event.target as HTMLInputElement;

    this.upload(slot).file = input.files && input.files[0] ? input.files[0] : null;
  }

  addBanner(slot: string, fileInput: HTMLInputElement): void {

    const u = this.upload(slot);

    if (!u.file || u.busy) {
      return;
    }

    const fd = new FormData();

    fd.append('file', u.file);
    fd.append('slot', slot);

    if (u.linkUrl.trim()) {
      fd.append('linkUrl', u.linkUrl.trim());
    }

    u.busy = true;

    this.http.post(`${this.host}/api/banners`, fd).subscribe({

      next: () => {

        u.busy = false;
        u.file = null;
        u.linkUrl = '';

        fileInput.value = '';

        this.bannerService.clear();

        this.loadBanners();

        this.toast(this.tr('✅ ব্যানার যোগ হয়েছে', '✅ Banner added'));
      },

      error: () => {

        u.busy = false;

        this.toast(this.tr('❌ ব্যানার আপলোড হয়নি', '❌ Banner upload failed'), true);
      }
    });
  }

  deleteBanner(b: any): void {

    if (!confirm(this.tr('এই ব্যানার মুছবেন?', 'Delete this banner?'))) {
      return;
    }

    this.http.delete(`${this.host}/api/banners/${b.id}`).subscribe({

      next: () => {

        this.bannerService.clear();

        this.loadBanners();

        this.toast(this.tr('🗑️ ব্যানার মুছে গেছে', '🗑️ Banner deleted'));
      },

      error: () =>
        this.toast(this.tr('❌ মুছতে পারেনি', '❌ Delete failed'), true)
    });
  }

  /* =========================================================
     পপুলার ক্যাটাগরি
  ========================================================= */

  imageUrl(url: string | null | undefined): string {

    if (!url) {
      return '';
    }

    if (url.startsWith('http')) {
      return url;
    }

    if (url.startsWith('/uploads')) {
      return `${this.host}${url}`;
    }

    return `${this.host}/images/${url}`;
  }

  catName(c: any): string {

    return this.languageService.translateCategory(c?.name, c?.nameBn);
  }

  loadPopular(): void {

    this.loadingPopular = true;

    this.http.get<any[]>(`${this.host}/api/categories/popular`).subscribe({

      next: res => {

        this.popular = (Array.isArray(res) ? res : []).map(c => ({
          ...c,
          popularOrder: Number(c.popularOrder) || 0
        }));

        this.loadingPopular = false;

        this.markResults();

        this.cdr.detectChanges();
      },

      error: () => {

        this.popular = [];
        this.loadingPopular = false;

        this.cdr.detectChanges();
      }
    });
  }

  onSearchInput(): void {

    clearTimeout(this.searchTimer);

    const q = this.search.trim();

    if (q.length < 2) {

      this.results = [];
      this.searched = false;
      this.searching = false;

      return;
    }

    this.searchTimer = setTimeout(() => this.runSearch(q), 350);
  }

  private runSearch(q: string): void {

    this.searching = true;

    this.http
      .get<any[]>(`${this.host}/api/categories/search`, { params: { q } })
      .subscribe({

        next: res => {

          this.results = Array.isArray(res) ? res : [];

          this.markResults();

          this.searching = false;
          this.searched = true;

          this.cdr.detectChanges();
        },

        error: () => {

          this.results = [];
          this.searching = false;
          this.searched = true;

          this.toast(
            this.tr(
              '❌ খুঁজতে পারেনি (ব্যাকএন্ড আপডেট হয়েছে তো?)',
              '❌ Search failed (is the backend updated?)'
            ),
            true
          );
        }
      });
  }

  // সার্চ রেজাল্টে যেগুলো আগে থেকেই পপুলার, সেগুলো চিহ্নিত
  private markResults(): void {

    const ids = new Set(this.popular.map(p => p.id));

    this.results = this.results.map(r => ({ ...r, popular: ids.has(r.id) }));
  }

  private nextOrder(): number {

    return this.popular.reduce(
      (m, c) => Math.max(m, Number(c.popularOrder) || 0),
      0
    ) + 1;
  }

  addPopular(c: any): void {

    this.setPopular(c, true, this.nextOrder());
  }

  removePopular(c: any): void {

    this.setPopular(c, false, 0);
  }

  private setPopular(c: any, popular: boolean, order: number): void {

    if (this.popularBusyId !== null) {
      return;
    }

    this.popularBusyId = c.id;

    this.http
      .put(
        `${this.host}/api/categories/${c.id}/popular`,
        null,
        { params: { popular: String(popular), order: String(order) } }
      )
      .subscribe({

        next: () => {

          this.popularBusyId = null;

          this.loadPopular();

          this.toast(
            popular
              ? this.tr('✅ পপুলারে যোগ হয়েছে', '✅ Added to popular')
              : this.tr('✅ পপুলার থেকে সরানো হয়েছে', '✅ Removed from popular')
          );
        },

        error: () => {

          this.popularBusyId = null;

          this.toast(this.tr('❌ হয়নি', '❌ Failed'), true);
        }
      });
  }

  savePopularOrder(c: any): void {

    if (this.popularBusyId !== null) {
      return;
    }

    this.popularBusyId = c.id;

    this.http
      .put(
        `${this.host}/api/categories/${c.id}/popular-order`,
        null,
        { params: { order: String(Math.max(0, Number(c.popularOrder) || 0)) } }
      )
      .subscribe({

        next: () => {

          this.popularBusyId = null;

          this.loadPopular();

          this.toast(this.tr('✅ ক্রম সেভ হয়েছে', '✅ Order saved'));
        },

        error: () => {

          this.popularBusyId = null;

          this.toast(this.tr('❌ হয়নি', '❌ Failed'), true);
        }
      });
  }

  trackKey(index: number, s: HomeSection): string {
    return s.sectionKey;
  }

  trackId(index: number, c: any): any {
    return c?.id ?? index;
  }

  hideBroken(event: Event): void {
    (event.target as HTMLImageElement).style.visibility = 'hidden';
  }
}
